import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PRODUCT_TYPES } from '../constants';
import { formatPrice, formatDate } from './index';

// Ana Renk - Bronz/Kahve
const PRIMARY_COLOR = [176, 67, 9]; // #b04309
const SECONDARY_COLOR = [143, 53, 14]; // #8f350e
const LIGHT_BG = [254, 247, 237]; // #fef7ed
const GRAY_TEXT = [100, 100, 100];

// Flemenkçe (Nederlands) Çeviri Sözlükleri
export const DUTCH_PRODUCT_TYPES = {
  tul: 'Vitrage',
  kanat: 'Overgordijn',
  stor: 'Rolgordijn',
  zebra: 'Zebra',
  ahsapJaluzi: 'Houten Jaloezie',
  metalJaluzi: 'Aluminium Jaloezie',
  dikeyPerde: 'Verticale Lamellen',
  dikeyPvc: 'Verticale PVC Lamellen',
  dikeyStor: 'Verticale Rolgordijnen',
  katlamaliPerde: 'Vouwgordijn',
  plise: 'Plisse',
  raySistemi: 'Gordijnrail',
  rustik: 'Roede',
  aksesuar: 'Accessoire'
};

export const DUTCH_PILE_MODELS = {
  'Standart SEYREK': 'Standaard Ruim',
  'Standart ORTA': 'Standaard Gemiddeld',
  'Standart SIK': 'Standaard Dicht',
  'S Pile': 'Waveplooi (S-plooi)',
  'Amerikan Pile Tekli': 'Enkele Plooi',
  "Amerikan Pile 2'li": 'Dubbele Plooi (Vlinder)',
  "Amerikan Pile 3'lü": 'Drievoudige Plooi',
  'Kapsül Pile': 'Ringen / Zeilogen',
  'Çima Dikiş': 'Doorgestikte zoom',
  'Düz Dikiş': 'Plat gestikt',
  'Kumaş Güneşlik': 'Verduisterend'
};

export const DUTCH_DIRECTIONS = {
  'Sol': 'Links',
  'Sağ': 'Rechts',
  'Sağa açılım': 'Naar rechts openend',
  'Sola açılım': 'Naar links openend',
  'Ortadan açılım': 'Midden openend'
};

export const getDutchProductType = (productType) => {
  return DUTCH_PRODUCT_TYPES[productType] || PRODUCT_TYPES[productType]?.label || productType || '';
};

// Türkçe karakter temizleme fonksiyonu
const cleanTurkishChars = (text) => {
  return (text || '').replace(/[ğ]/g, 'g')
    .replace(/[Ğ]/g, 'G')
    .replace(/[ü]/g, 'u')
    .replace(/[Ü]/g, 'U')
    .replace(/[ş]/g, 's')
    .replace(/[Ş]/g, 'S')
    .replace(/[ı]/g, 'i')
    .replace(/[İ]/g, 'I')
    .replace(/[ö]/g, 'o')
    .replace(/[Ö]/g, 'O')
    .replace(/[ç]/g, 'c')
    .replace(/[Ç]/g, 'C');
};

// Modern Header çiz
const drawHeader = (doc, title, subTitle = '') => {
  // Üst banner
  doc.setFillColor(...PRIMARY_COLOR);
  doc.rect(0, 0, 210, 35, 'F');

  // Firma adı
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EBRAR GORDIJNEN', 20, 18);

  // Alt başlık / Müşteri Adı
  if (subTitle) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(cleanTurkishChars(subTitle).toUpperCase(), 20, 29);
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanTurkishChars(title), 20, 28);
  }

  // Sağ taraf - İletişim (Hollanda Mağaza Bilgileri)
  doc.setFontSize(9);
  doc.text('Tel: +31 6 24 43 81 95', 190, 14, { align: 'right' });
  doc.text('ebrar-huistextiel@hotmail.nl', 190, 22, { align: 'right' });
  doc.text('Kaapstraat 12, 2572 HJ Den Haag', 190, 30, { align: 'right' });

  // Reset text color
  doc.setTextColor(0, 0, 0);
};

// Modern Footer çiz (Flemenkçe)
const drawFooter = (doc) => {
  const pageHeight = doc.internal.pageSize.height;

  // Alt çizgi
  doc.setDrawColor(...PRIMARY_COLOR);
  doc.setLineWidth(0.5);
  doc.line(20, pageHeight - 25, 190, pageHeight - 25);

  // Footer text
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text('Ebrar Gordijnen - Kwalitatieve Gordijnoplossingen', 105, pageHeight - 18, { align: 'center' });
  doc.text('Dit document is computer gegenereerd.', 105, pageHeight - 12, { align: 'center' });

  // Sayfa numarası
  const pageCount = doc.internal.getNumberOfPages();
  doc.text(`Pagina ${doc.internal.getCurrentPageInfo().pageNumber} / ${pageCount}`, 190, pageHeight - 12, { align: 'right' });

  doc.setTextColor(0, 0, 0);
};

// Metni satırlara böl
const splitTextToLines = (text, maxCharsPerLine) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    if ((currentLine + ' ' + word).trim().length <= maxCharsPerLine) {
      currentLine = (currentLine + ' ' + word).trim();
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);
  return lines;
};

/** Atölye PDF kutusunda gösterilecek ek satırları oluşturur (not hariç) */
const buildWorkshopOrderExtraLines = (order) => {
  const items = [];

  const pushSpacer = () => {
    if (items.length > 0) items.push({ spacer: true });
  };

  if (order.productType === 'tul' && order.altEtekKatlama) {
    items.push({ text: 'Onderzoom omslag', color: 'primary' });
  }

  if ((order.productType === 'tul' || order.productType === 'kanat') && order.ortadanIkiParca) {
    items.push({ text: 'UYARI: 2 parça olacaktır (2 delen)', color: 'primary', bold: true });
  }

  if (order.productType === 'kanat') {
    const hasOzel = (order.hasOzelTasarim || order.ozelProductName) && order.ozelProductName;
    if (hasOzel) {
      pushSpacer();
      let ozelLine = `Speciaal ontwerp: ${order.ozelProductName}`;
      if (order.ozelProductVariant) ozelLine += ` - ${order.ozelProductVariant}`;
      items.push({ text: ozelLine, color: 'primary', bold: true });

      if (order.ozelPileModel) {
        const dPlooi = DUTCH_PILE_MODELS[order.ozelPileModel] || order.ozelPileModel;
        items.push({ text: `Plooi: ${dPlooi}`, color: 'primary' });
      }

      const dims = [];
      if (order.ozelWidth) dims.push(`Breedte: ${order.ozelWidth}`);
      if (order.ozelHeight) dims.push(`Hoogte: ${order.ozelHeight}`);
      const dimPart = dims.length ? dims.join('  ') : '';
      const adetPart = `Aantal: ${order.ozelQuantity || '1'}`;
      items.push({
        text: dimPart ? `${dimPart}  ${adetPart}` : adetPart,
        color: 'primary'
      });
    }

    const hasAstar = (order.hasAstar || order.astarProductName) && order.astarProductName;
    if (hasAstar) {
      pushSpacer();
      let astarLine = `Voering: ${order.astarProductName}`;
      if (order.astarProductVariant) astarLine += ` - ${order.astarProductVariant}`;
      items.push({ text: astarLine, color: 'black' });
    }

    const accs = Array.isArray(order.accessories) ? order.accessories.filter((a) => a.name) : [];
    if (accs.length > 0) {
      pushSpacer();
      accs.forEach((acc) => {
        const isMetre = acc.unit === 'metre';
        const detail = isMetre
          ? `${acc.metre || '1'} m x ${acc.quantity || '1'} stuks`
          : `${acc.quantity || '1'} stuks`;
        items.push({ text: `Accessoire: ${acc.name} (${detail})`, color: 'black' });
      });
    }
  }

  return items.map((item) => {
    if (item.spacer) return item;
    return { ...item, text: cleanTurkishChars(item.text) };
  });
};

/** Atölye PDF kutusu yüksekliğini içeriğe göre hesaplar */
const calcWorkshopBoxHeight = (extraItems, noteLines) => {
  let lineCount = 0;
  extraItems.forEach((item) => {
    if (item.spacer) {
      lineCount += 1;
      return;
    }
    const wrapped = splitTextToLines(item.text, 42);
    lineCount += Math.min(wrapped.length, 2);
  });
  const noteLineCount = Math.min(noteLines.length, 3);
  const noteBlockHeight = noteLineCount > 0 ? (extraItems.length > 0 ? 12 : 7) + noteLineCount * 5 : 0;
  const innerHeight = 26 + lineCount * 5 + noteBlockHeight;
  return Math.max(63, 14 + innerHeight + 6);
};

// Müşteri bilgileri kutusu (Flemenkçe)
const drawCustomerInfoBox = (doc, customerInfo, startY) => {
  const cleanName = cleanTurkishChars(customerInfo.name || '');
  const cleanAddress = cleanTurkishChars(customerInfo.address || '');

  // Adresi satırlara böl (maks 60 karakter per satır)
  const addressLines = splitTextToLines(cleanAddress, 60);
  const extraLines = Math.max(0, addressLines.length - 1);
  const boxHeight = 45 + (extraLines * 7);

  // Kutu arka planı
  doc.setFillColor(...LIGHT_BG);
  doc.roundedRect(15, startY, 180, boxHeight, 3, 3, 'F');

  // Sol taraf - Müşteri bilgileri
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('KLANTGEGEVENS', 20, startY + 10);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Naam: ${cleanName}`, 20, startY + 20);
  doc.text(`Telefoon: ${customerInfo.phone1 || '-'}`, 20, startY + 28);

  // Adres satırları
  let addressY = startY + 36;
  addressLines.forEach((line, index) => {
    const prefix = index === 0 ? 'Adres: ' : '       ';
    doc.text(`${prefix}${line}`, 20, addressY);
    addressY += 7;
  });

  // Sağ taraf - Tarihler
  if (customerInfo.orderDate || customerInfo.deliveryDate || customerInfo.discoveryDate) {
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);

    let dateY = startY + 20;
    if (customerInfo.orderDate) {
      doc.text(`Besteldatum: ${formatDate(customerInfo.orderDate)}`, 190, dateY, { align: 'right' });
      dateY += 8;
    }

    if (customerInfo.deliveryDate) {
      doc.text(`Leverdatum: ${formatDate(customerInfo.deliveryDate)}`, 190, dateY, { align: 'right' });
      dateY += 8;
    }

    if (customerInfo.discoveryDate) {
      doc.text(`Inmeetdatum: ${formatDate(customerInfo.discoveryDate)}`, 190, dateY, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 8;
};

// Toplam tutar kutusu - Sağda alt alta format (Flemenkçe & Euro)
const drawTotalBox = (doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, startY, showPaymentInfo = false, paidAmount = 0, remainingAmount = 0, showDetailedPricing = true) => {
  const vYuzde = parseFloat(vadeliYuzde) || 0;
  const gIskonto = parseFloat(genelIskontoYuzde) || 0;

  const hasVade = vYuzde > 0;
  const hasIskonto = gIskonto > 0;
  const hasProductDiscount = discountedTotalAmount !== totalAmount && discountedTotalAmount > 0;

  // Hesaplamalar
  const vadeliToplam = totalAmount + (totalAmount * vYuzde / 100);
  const iskontoluToplam = totalAmount - (totalAmount * gIskonto / 100);

  // Hiyerarşi (Genel Toplam): Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
  let grandTotal = totalAmount;
  if (hasVade) grandTotal = vadeliToplam;
  else if (hasIskonto) grandTotal = iskontoluToplam;
  else if (hasProductDiscount) grandTotal = discountedTotalAmount;

  // Kutu yüksekliğini hesapla
  let itemCount = 1; // Sadece Genel toplam (alt sınır)
  if (showDetailedPricing) {
    itemCount += 1; // Ara toplam
    if (hasProductDiscount) itemCount++;
    if (hasIskonto) itemCount += 2; // % + tutar
    if (hasVade) itemCount += 2; // % + tutar
  }

  if (showPaymentInfo) {
    itemCount += 2; // Ödenen + Kalan
  }
  const boxHeight = 20 + (itemCount * 10);

  // Ana kutu
  doc.setFillColor(...PRIMARY_COLOR);
  doc.roundedRect(100, startY, 95, boxHeight, 3, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let lineY = startY + 12;

  if (showDetailedPricing) {
    // Subtotaal
    doc.text('Subtotaal:', 105, lineY);
    doc.text(`${formatPrice(totalAmount)} €`, 190, lineY, { align: 'right' });
    lineY += 9;

    // Totaal na productkorting
    if (hasProductDiscount) {
      doc.text('Totaal na productkorting:', 105, lineY);
      doc.text(`${formatPrice(discountedTotalAmount)} €`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Korting
    if (hasIskonto) {
      doc.text(`Korting (%${genelIskontoYuzde}):`, 105, lineY);
      doc.text(`-${formatPrice(totalAmount * gIskonto / 100)} €`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Totaal na korting:', 105, lineY);
      doc.text(`${formatPrice(iskontoluToplam)} €`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Termijntoelage
    if (hasVade) {
      doc.text(`Termijntoelage (%${vadeliYuzde}):`, 105, lineY);
      doc.text(`+${formatPrice(totalAmount * vYuzde / 100)} €`, 190, lineY, { align: 'right' });
      lineY += 9;

      doc.text('Termijntotaal:', 105, lineY);
      doc.text(`${formatPrice(vadeliToplam)} €`, 190, lineY, { align: 'right' });
      lineY += 9;
    }

    // Ayırıcı çizgi
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);
  }

  // TOTAAL (Netto)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('TOTAAL (Netto):', 105, lineY + 6);
  doc.text(`${formatPrice(grandTotal)} €`, 190, lineY + 6, { align: 'right' });
  lineY += 14;

  // Betaald en Openstaand
  if (showPaymentInfo) {
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.3);
    doc.line(105, lineY - 2, 190, lineY - 2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    // Betaald
    doc.text('Betaald:', 105, lineY + 4);
    doc.text(`${formatPrice(paidAmount)} €`, 190, lineY + 4, { align: 'right' });
    lineY += 9;

    // Openstaand
    const isPaymentComplete = Math.abs(remainingAmount) < 0.01 || remainingAmount <= 0;
    doc.setFont('helvetica', 'bold');
    doc.text('Openstaand:', 105, lineY + 4);
    if (isPaymentComplete) {
      doc.text('BETAALD', 190, lineY + 4, { align: 'right' });
    } else {
      doc.text(`${formatPrice(remainingAmount)} €`, 190, lineY + 4, { align: 'right' });
    }
  }

  doc.setTextColor(0, 0, 0);
  return startY + boxHeight + 10;
};

// Müşteri PDF tablo verisini oluşturur (Flemenkçe)
const buildCustomerTableData = (orders, includeMeasurements = true) => {
  const head = includeMeasurements
    ? [['Producttype', 'Ruimte', 'Productnaam', 'Afmetingen (cm)', 'Plooimodel', 'Aantal', 'Prijs (€)']]
    : [['Producttype', 'Ruimte', 'Productnaam', 'Plooimodel', 'Aantal', 'Prijs (€)']];

  const body = orders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(getDutchProductType(order.productType));
    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    const cleanPileModel = cleanTurkishChars(DUTCH_PILE_MODELS[order.pileModel] || order.pileModel || '');
    const discountRate = parseFloat(order.discount) || 0;

    // Plise için özel satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisysteem)' : '';
        const cleanProductName = item.multisistem ? (p1Base + p2Base + cleanTurkishChars(ms)) : p1Base;

        const row = [
          cleanProductType,
          cleanRoomName,
          cleanProductName
        ];

        if (includeMeasurements) {
          row.push(`${item.width || '-'} x ${item.height || '-'}`);
        }

        const rowTotal = parseFloat(item.rowTotal) || 0;
        const discountedRowTotal = rowTotal * (1 - discountRate / 100);
        let plisePriceDisplay = formatPrice(rowTotal);
        if (discountRate > 0) {
          plisePriceDisplay += `\n${formatPrice(discountedRowTotal)} (%${discountRate})`;
        }

        row.push(
          '-',
          item.quantity || '1',
          plisePriceDisplay
        );

        return row;
      });
    }

    // Diğer ürün tipleri için tek satır
    let productName = order.productName || '';
    if (order.productVariant) {
      productName += ` - ${order.productVariant}`;
    }
    const directionStr = order.direction ? (DUTCH_DIRECTIONS[order.direction] || order.direction) : '';
    const cleanProductName = cleanTurkishChars(productName) + (directionStr ? cleanTurkishChars(` (${directionStr})`) : '');

    const subDetailsLines = [];

    // Vitrage Accessoires
    const tulAccs = order.productType === 'tul' ? (order.tulAccessories || order.accessories || order.kanatAccessories || []) : [];
    if (order.productType === 'tul' && tulAccs.length > 0) {
      const accText = tulAccs.map((a) => {
        const isMetre = a.unitType === 'metre' || a.unit === 'metre';
        const meterVal = a.metre ?? a.meter ?? '';
        const priceVal = a.total ?? a.totalPrice ?? a.price ?? 0;
        const qtyVal = a.quantity || 1;

        if (includeMeasurements) {
          const detail = isMetre
            ? `${meterVal} m x ${qtyVal} stuks`
            : `${qtyVal} stuks`;
          return `${a.name} (${detail} - ${formatPrice(priceVal)} €)`;
        } else {
          return `${a.name} (${qtyVal} stuks - ${formatPrice(priceVal)} €)`;
        }
      }).join(', ');
      subDetailsLines.push(cleanTurkishChars(`Accessoires: ${accText}`));
    }

    // Kanat Özel Tasarım ve Aksesuarlar
    if (order.productType === 'kanat') {
      const cd = order.customDesign || (order.hasOzelTasarim ? {
        productName: order.ozelTasarimProductName || order.ozelProductName || '',
        productVariant: order.ozelTasarimProductVariant || order.ozelProductVariant || '',
        width: order.ozelTasarimWidth || order.ozelWidth || '',
        height: order.ozelTasarimHeight || order.ozelHeight || '',
        quantity: order.ozelTasarimQuantity || order.ozelQuantity || '1',
        totalPrice: order.ozelTasarimTotalPrice || order.ozelTotalPrice || order.ozelTasarimTotal || 0
      } : null);

      if (cd && cd.productName) {
        const variantText = cd.productVariant ? ` (${cd.productVariant})` : '';
        const sizeText = (includeMeasurements && (cd.width || cd.height)) ? ` | ${cd.width}x${cd.height} cm` : '';
        const priceVal = cd.totalPrice || cd.price || 0;
        const priceText = priceVal ? ` | Bedrag: ${formatPrice(priceVal)} €` : '';
        const cdText = `Speciaal ontwerp: ${cd.productName}${variantText}${sizeText} | ${cd.quantity || '1'} stuks${priceText}`;
        subDetailsLines.push(cleanTurkishChars(cdText));
      }

      if (order.hasAstar && order.astarProductName) {
        const variantText = order.astarProductVariant ? ` (${order.astarProductVariant})` : '';
        const priceText = order.astarPrice ? ` | Bedrag: ${formatPrice(order.astarPrice)} €` : '';
        subDetailsLines.push(cleanTurkishChars(`Voering: ${order.astarProductName}${variantText}${priceText}`));
      }

      const kanatAccs = order.kanatAccessories || order.accessories || order.tulAccessories || [];
      if (kanatAccs.length > 0) {
        const accText = kanatAccs.map((a) => {
          const isMetre = a.unitType === 'metre' || a.unit === 'metre';
          const meterVal = a.metre ?? a.meter ?? '';
          const priceVal = a.total ?? a.totalPrice ?? a.price ?? 0;
          const qtyVal = a.quantity || 1;

          if (includeMeasurements) {
            const detail = isMetre
              ? `${meterVal} m x ${qtyVal} stuks`
              : `${qtyVal} stuks`;
            return `${a.name} (${detail} - ${formatPrice(priceVal)} €)`;
          } else {
            return `${a.name} (${qtyVal} stuks - ${formatPrice(priceVal)} €)`;
          }
        }).join(', ');
        subDetailsLines.push(cleanTurkishChars(`Accessoires: ${accText}`));
      }
    }

    const mainRow = [
      cleanProductType,
      cleanRoomName,
      cleanProductName
    ];

    if (includeMeasurements) {
      mainRow.push(`${order.width || ''} x ${order.height || ''}`);
    }

    const hasDiscount = order.discountedPrice && discountRate > 0;
    let priceDisplay = formatPrice(order.totalPrice || 0);
    if (hasDiscount) {
      priceDisplay += `\n${formatPrice(order.discountedPrice)} (%${discountRate})`;
    }

    mainRow.push(
      cleanPileModel,
      order.quantity || '1',
      priceDisplay
    );

    // Ray Sistemi için kullanılan metriği olculer sütununda göster (Müşteri PDF)
    if (order.productType === 'raySistemi' && includeMeasurements) {
      const usedMeter = order.usedMeter ? `${order.usedMeter} m` : '-';
      const olcu = order.olcuTamamlama ? ` (${order.olcuTamamlama})` : '';
      mainRow[4] = cleanTurkishChars(order.olcuTamamlama || '-');
      mainRow[3] = `${order.width || ''} cm\nGebruikt: ${usedMeter}${olcu}`;
    }

    // Rustik için detayları göster
    if (order.productType === 'rustik' && includeMeasurements) {
      const usedCm = order.usedCm ? `${order.usedCm} cm` : '-';
      mainRow[3] = `Breedte: ${order.width || ''} cm\nGebruikt: ${usedCm}`;

      let ayakDetay = cleanTurkishChars(order.ayakModel || '-');
      if (order.ayakSayisi) {
        ayakDetay += `\nAantal: ${order.ayakSayisi}`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Extra)`;
      }
      if (order.ayakCap || order.ayakRenkKodu) {
        ayakDetay += `\n(${order.ayakCap || ''} ${cleanTurkishChars(order.ayakRenkKodu || '')})`;
      }
      mainRow[4] = ayakDetay;
    }

    const rows = [mainRow];
    if (subDetailsLines.length > 0) {
      const totalCols = includeMeasurements ? 7 : 6;
      rows.push([
        {
          content: subDetailsLines.join('\n'),
          colSpan: totalCols,
          styles: {
            halign: 'left',
            fillColor: [248, 250, 252],
            fontStyle: 'italic',
            textColor: [71, 85, 105],
            fontSize: 8.5,
            cellPadding: 4
          }
        }
      ]);
    }

    return rows;
  });

  return { head, body };
};


// Ana Müşteri PDF oluşturma
const generateCustomerPDFBase = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, options = {}) => {
  const includeMeasurements = options.includeMeasurements !== false;
  const title = options.title || 'Bestelbon';
  const showPaymentInfo = options.showPaymentInfo || false;
  const showDetailedPricing = options.showDetailedPricing !== false; // Varsayılan true
  const paidAmount = options.paidAmount || 0;
  const remainingAmount = options.remainingAmount || 0;

  if (!customerInfo || orders.length === 0) {
    alert('Geen klantgegevens of bestelling gevonden!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, title);

  // Müşteri bilgileri
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş tablosu başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('BESTELDETAILS', 20, currentY);
  currentY += 8;

  // Tablo verilerini hazırla
  const { head, body } = buildCustomerTableData(orders, includeMeasurements);

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head,
    body,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center'
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: includeMeasurements
      ? {
        0: { cellWidth: 22 },  // Producttype
        1: { cellWidth: 18 },  // Ruimte
        2: { cellWidth: 48 },  // Productnaam (genişletildi)
        3: { cellWidth: 25 },  // Afmetingen(cm)
        4: { cellWidth: 25 },  // Plooimodel
        5: { cellWidth: 14, halign: 'center' },  // Aantal
        6: { cellWidth: 28, halign: 'right' }   // Prijs
      }
      : {
        0: { cellWidth: 25 },  // Producttype
        1: { cellWidth: 22 },  // Ruimte
        2: { cellWidth: 58 },  // Productnaam (genişletildi)
        3: { cellWidth: 30 },  // Plooimodel
        4: { cellWidth: 15, halign: 'center' },  // Aantal
        5: { cellWidth: 30, halign: 'right' }   // Prijs
      },
    pageBreak: 'auto',
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, title + ' (Vervolg)');
      }
    }
  });

  // Toplam tutar kutusu
  let finalY = doc.lastAutoTable.finalY + 10;

  // Toplam kutusunun tahmini yüksekliğini hesapla
  let estimatedBoxItems = 1; // Algemeen totaal
  if (showDetailedPricing) {
    estimatedBoxItems += 1; // Subtotaal
    if (discountedTotalAmount !== totalAmount) estimatedBoxItems++;
    if (vadeliYuzde && parseFloat(vadeliYuzde) > 0) estimatedBoxItems += 2;
    if (genelIskontoYuzde && parseFloat(genelIskontoYuzde) > 0) estimatedBoxItems += 2;
  }
  if (showPaymentInfo) estimatedBoxItems += 2;
  const estimatedBoxHeight = 20 + (estimatedBoxItems * 10);

  // Sayfa kontrolü
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + estimatedBoxHeight > pageHeight - 40) {
    doc.addPage();
    drawHeader(doc, title);
    finalY = 50;
  }

  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, showPaymentInfo, paidAmount, remainingAmount, showDetailedPricing);

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Müşteri PDF oluşturma (Bestelbon)
export const generateCustomerPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: true,
    title: 'Bestelbon',
    showPaymentInfo: true,
    showDetailedPricing: false,
    paidAmount,
    remainingAmount
  });
};

// Müşteri PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, paidAmount = 0, remainingAmount = 0) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Bestelbon');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('BESTELDETAILS', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, true);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 48 }, 3: { cellWidth: 25 }, 4: { cellWidth: 25 }, 5: { cellWidth: 14, halign: 'center' }, 6: { cellWidth: 28, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Bestelbon (Vervolg)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 80 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Bestelbon'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, true, paidAmount, remainingAmount, false);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Müşteri (Sipariş öncesi) PDF oluşturma - en/boy bilgisi olmadan (Offerte)
export const generateCustomerPreOrderPDF = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  return generateCustomerPDFBase(customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, {
    includeMeasurements: false,
    title: 'Offerte / Prijsopgave',
    showPaymentInfo: false,
    showDetailedPricing: true
  });
};

// Müşteri (Sipariş öncesi) PDF Blob döndür (WhatsApp paylaşımı için)
export const generateCustomerPreOrderPDFBlob = (customerInfo, orders, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde) => {
  if (!customerInfo || orders.length === 0) return null;
  const doc = new jsPDF();
  doc.setFont('helvetica');
  drawHeader(doc, 'Offerte / Prijsopgave');
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PRIMARY_COLOR);
  doc.text('BESTELDETAILS', 20, currentY); currentY += 8;
  const { head, body } = buildCustomerTableData(orders, false);
  autoTable(doc, {
    startY: currentY, head, body, theme: 'striped',
    headStyles: { fillColor: PRIMARY_COLOR, textColor: 255, fontStyle: 'bold', fontSize: 9, halign: 'center' },
    styles: { fontSize: 9, cellPadding: 4, halign: 'center' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: { 0: { cellWidth: 25 }, 1: { cellWidth: 22 }, 2: { cellWidth: 58 }, 3: { cellWidth: 30 }, 4: { cellWidth: 15, halign: 'center' }, 5: { cellWidth: 30, halign: 'right' } },
    pageBreak: 'auto', margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, 'Offerte / Prijsopgave (Vervolg)'); }
  });
  let finalY = doc.lastAutoTable.finalY + 10;
  const pageHeight = doc.internal.pageSize.height;
  if (finalY + 60 > pageHeight - 40) { doc.addPage(); drawHeader(doc, 'Offerte / Prijsopgave'); finalY = 50; }
  drawTotalBox(doc, totalAmount, discountedTotalAmount, genelIskontoYuzde, vadeliYuzde, finalY, false, 0, 0, true);
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) { doc.setPage(i); drawFooter(doc); }
  return doc.output('blob');
};

// Atölye PDF oluşturma (Atelierbon)
export const generateWorkshopPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Geen klantgegevens of bestelling gevonden!');
    return;
  }

  const tulKanatOrders = orders.filter(order =>
    order.productType === 'tul' || order.productType === 'kanat'
  );

  if (tulKanatOrders.length === 0) {
    alert('Geen vitrage of overgordijn gevonden voor atelierbon!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header - Müşteri adını büyük başlık olarak yazdır
  drawHeader(doc, 'Atelierbon', customerInfo.name);

  // Siparişler için kutular
  const cleanCustomerName = cleanTurkishChars(customerInfo.name);
  let currentY = 45;
  let currentX = 12;
  const boxWidth = 90;
  const margin = 6;
  let rowMaxHeight = 0;

  tulKanatOrders.forEach((order) => {
    const cleanNotes = cleanTurkishChars(order.notes || '');
    const extraItems = buildWorkshopOrderExtraLines(order);
    const noteLines = cleanNotes ? splitTextToLines(cleanNotes, 42) : [];
    const boxHeight = calcWorkshopBoxHeight(extraItems, noteLines);

    if (currentY + boxHeight > 270) {
      doc.addPage();
      drawHeader(doc, 'Atelierbon', customerInfo.name);
      currentY = 45;
      currentX = 12;
    }

    // Kutu arka planı
    doc.setFillColor(...LIGHT_BG);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'F');

    // Kutu kenarlığı
    doc.setDrawColor(...PRIMARY_COLOR);
    doc.setLineWidth(0.5);
    doc.roundedRect(currentX, currentY, boxWidth, boxHeight, 3, 3, 'S');

    // Üst şerit - müşteri adı + oda adı
    doc.setFillColor(...PRIMARY_COLOR);
    doc.roundedRect(currentX, currentY, boxWidth, 14, 3, 3, 'F');
    doc.rect(currentX, currentY + 7, boxWidth, 7, 'F');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);

    const cleanRoomName = cleanTurkishChars(order.roomName || '');
    doc.text(cleanCustomerName.substring(0, 25), currentX + 4, currentY + 9);
    doc.setFontSize(8);
    doc.text(cleanRoomName, currentX + boxWidth - 4, currentY + 9, { align: 'right' });

    // İçerik
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);

    let fullProductName = order.productName || '';
    if (order.productVariant) fullProductName += ` - ${order.productVariant}`;
    const cleanProductName = cleanTurkishChars(fullProductName) + (order.direction ? cleanTurkishChars(` (${order.direction})`) : '');
    const cleanPileModel = cleanTurkishChars(DUTCH_PILE_MODELS[order.pileModel] || order.pileModel || '');

    doc.text(`Product: ${cleanProductName.substring(0, 28)}`, currentX + 4, currentY + 20);
    doc.text(`Plooi: ${cleanPileModel.substring(0, 25)}`, currentX + 4, currentY + 28);
    doc.setFont('helvetica', 'bold');
    doc.text(`Breedte: ${order.width || '-'} cm  Hoogte: ${order.height || '-'} cm  Aantal: ${order.quantity || '1'}`, currentX + 4, currentY + 36);

    // Ek bilgiler (onderzoom omslag, speciaal ontwerp, voering, accessoire)
    let contentY = currentY + 44;
    if (extraItems.length > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      extraItems.forEach((item) => {
        if (item.spacer) {
          contentY += 5;
          return;
        }
        const textColor = item.color === 'primary' ? PRIMARY_COLOR : [0, 0, 0];
        doc.setTextColor(...textColor);
        doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
        const wrapped = splitTextToLines(item.text, 42);
        wrapped.slice(0, 2).forEach((wrappedLine) => {
          doc.text(wrappedLine, currentX + 4, contentY);
          contentY += 5;
        });
      });
      doc.setTextColor(0, 0, 0);
    }

    // Not alanı
    if (cleanNotes) {
      if (extraItems.length > 0) contentY += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_TEXT);
      doc.text('Opmerking:', currentX + 4, contentY);
      contentY += 5;
      noteLines.slice(0, 3).forEach((line) => {
        doc.text(line, currentX + 4, contentY);
        contentY += 5;
      });
    }

    doc.setTextColor(0, 0, 0);

    // Sonraki kutu pozisyonu
    rowMaxHeight = Math.max(rowMaxHeight, boxHeight);
    currentX += boxWidth + margin;

    if (currentX + boxWidth > 200) {
      currentX = 12;
      currentY += rowMaxHeight + 10;
      rowMaxHeight = 0;
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Montaj PDF oluşturma (Montagebon)
export const generateInstallationPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Geen klantgegevens of bestelling gevonden!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Montagebon');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Sipariş listesi başlığı
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PRIMARY_COLOR);
  doc.text('MONTAGELIJST', 20, currentY);
  currentY += 8;

  // Siparişleri oda gruplarına göre sırala
  const sortedOrders = [...orders].sort((a, b) => {
    const roomGroupA = (a.roomName || '').substring(0, 3).toUpperCase();
    const roomGroupB = (b.roomName || '').substring(0, 3).toUpperCase();

    if (roomGroupA !== roomGroupB) {
      return roomGroupA.localeCompare(roomGroupB, 'nl-NL');
    }

    const roomCompare = (a.roomName || '').localeCompare(b.roomName || '', 'nl-NL');
    if (roomCompare !== 0) return roomCompare;

    return (a.productType || '').localeCompare(b.productType || '', 'nl-NL');
  });

  // Tablo verileri
  const tableData = sortedOrders.flatMap((order) => {
    const cleanProductType = cleanTurkishChars(getDutchProductType(order.productType));
    const cleanRoomName = cleanTurkishChars(order.roomName || '');

    // Plise için detaylı satır bazlı genişletme
    if (order.productType === 'plise' && Array.isArray(order.pliseItems) && order.pliseItems.length > 0) {
      const p1Base = cleanTurkishChars(order.pliseProduct1Name || '');
      const p2Base = order.pliseProduct2Name ? ` + ${cleanTurkishChars(order.pliseProduct2Name)}` : '';

      return order.pliseItems.map((item) => {
        const ms = item.multisistem ? ' (Multisysteem)' : '';
        const namePart = item.multisistem ? (p1Base + p2Base + ms) : p1Base;
        const dimensionPart = ` [${item.width} x ${item.height}] - ${item.quantity} stuks`;
        const fullProductName = namePart + cleanTurkishChars(dimensionPart);

        return [
          cleanRoomName,
          cleanProductType,
          fullProductName
        ];
      });
    }

    // Diğer ürünler için
    let productName = order.productName || '';
    if (order.productVariant) productName += ` - ${order.productVariant}`;
    const directionStr = order.direction ? (DUTCH_DIRECTIONS[order.direction] || order.direction) : '';
    const cleanProductName = cleanTurkishChars(productName) + (directionStr ? cleanTurkishChars(` (${directionStr})`) : '');

    return [[
      cleanRoomName,
      cleanProductType,
      cleanProductName
    ]];
  });

  // Tablo oluştur
  autoTable(doc, {
    startY: currentY,
    head: [['Ruimte', 'Producttype', 'Productnaam']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: PRIMARY_COLOR,
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 10
    },
    styles: {
      fontSize: 10,
      cellPadding: 5
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250]
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 45 },
      2: { cellWidth: 75 }
    },
    margin: { top: 45, right: 15, bottom: 35, left: 15 },
    didDrawPage: function (data) {
      if (data.pageNumber > 1) {
        drawHeader(doc, 'Montagebon (Vervolg)');
      }
    }
  });

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

// Firma PDF oluşturma (Fabrikant Bestelbon)
export const generateFirmaPDF = (customerInfo, orders) => {
  if (!customerInfo || orders.length === 0) {
    alert('Geen klantgegevens of bestelling gevonden!');
    return;
  }

  const doc = new jsPDF();
  doc.setFont('helvetica');

  // Header
  drawHeader(doc, 'Fabrikant Bestelbon');

  // Müşteri bilgileri kutusu
  let currentY = drawCustomerInfoBox(doc, customerInfo, 45);

  // Siparişleri ürün tipine göre ayır
  const tulKanatOrders = orders.filter(order => order.productType === 'tul' || order.productType === 'kanat');
  const otherOrders = orders.filter(order => !['tul', 'kanat'].includes(order.productType));

  // Vitrage en overgordijnen tablosu
  if (tulKanatOrders.length > 0) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('VITRAGE EN OVERGORDIJNEN', 20, currentY);

    const tulKanatTableData = tulKanatOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(getDutchProductType(order.productType));

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const directionStr = order.direction ? (DUTCH_DIRECTIONS[order.direction] || order.direction) : '';
      const cleanProductName = cleanTurkishChars(productName) + (directionStr ? cleanTurkishChars(` (${directionStr})`) : '');

      return [
        cleanProductType,
        cleanProductName,
        `${order.usedM2 || ''} m`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Producttype', 'Productnaam', 'Gebruikt m', 'Aantal']],
      body: tulKanatTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60 },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Fabrikant Bestelbon (Vervolg)');
        }
      }
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  // Diğer ürünler tablosu (Mechanische Producten)
  if (otherOrders.length > 0) {
    if (currentY > 200) {
      doc.addPage();
      drawHeader(doc, 'Fabrikant Bestelbon (Vervolg)');
      currentY = 50;
    }

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text('MECHANISCHE PRODUCTEN', 20, currentY);

    const otherTableData = otherOrders.map((order) => {
      const cleanProductType = cleanTurkishChars(getDutchProductType(order.productType));

      let productName = order.productName || '';
      if (order.productType === 'plise') {
        productName = order.pliseProduct1Name || '';
        if (order.pliseProduct2Name) {
          productName += ` + ${order.pliseProduct2Name}`;
        }
      } else if (order.productVariant) {
        productName += ` - ${order.productVariant}`;
      }

      const directionStr = order.direction ? (DUTCH_DIRECTIONS[order.direction] || order.direction) : '';
      const cleanProductName = cleanTurkishChars(productName) + (directionStr ? cleanTurkishChars(` (${directionStr})`) : '');

      let finalProductName = cleanProductName;
      if (order.productType === 'rustik') {
        let ayakDetay = ` [Steun: ${cleanTurkishChars(order.ayakModel || '')}`;
        if (order.ayakRenkKodu) ayakDetay += ` - ${cleanTurkishChars(order.ayakRenkKodu)}`;
        ayakDetay += ` - ${order.ayakSayisi || 0} stuks`;
        if (order.ekAyakSayisi) ayakDetay += ` (+${order.ekAyakSayisi} Extra)`;
        ayakDetay += ']';
        finalProductName += ayakDetay;

        // Accessoires en Rails
        if (Array.isArray(order.rustikAksesuarlar) && order.rustikAksesuarlar.length > 0) {
          finalProductName += '\n Accessoires: ' + order.rustikAksesuarlar.map(a => `${cleanTurkishChars(a.name)} (${a.quantity})`).join(', ');
        }
        if (Array.isArray(order.rustikRaySecenekleri) && order.rustikRaySecenekleri.length > 0) {
          finalProductName += '\n Rails: ' + order.rustikRaySecenekleri.map(r => `${cleanTurkishChars(r.name)} (${r.quantity})`).join(', ');
        }
      }

      const isRay = order.productType === 'raySistemi';
      const isRustik = order.productType === 'rustik';

      const usedVal = isRay ? (order.usedMeter ? `${order.usedMeter} m` : '-')
        : isRustik ? (order.usedCm ? `${order.usedCm} cm` : '-')
          : (order.usedM2 ? `${order.usedM2} m2` : '-');

      const spec = isRay && order.olcuTamamlama ? `\n(${cleanTurkishChars(order.olcuTamamlama)})` : '';

      return [
        cleanProductType,
        finalProductName,
        `${order.width || ''}`,
        (isRay || isRustik) ? '-' : `${order.height || ''}`,
        `${usedVal}${spec}`,
        order.quantity || '1'
      ];
    });

    autoTable(doc, {
      startY: currentY + 8,
      head: [['Producttype', 'Productnaam', 'Breedte (cm)', 'Hoogte (cm)', 'Gebruikt m2', 'Aantal']],
      body: otherTableData,
      theme: 'striped',
      headStyles: {
        fillColor: PRIMARY_COLOR,
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 50 },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' }
      },
      margin: { top: 45, right: 15, bottom: 35, left: 15 },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          drawHeader(doc, 'Fabrikant Bestelbon (Vervolg)');
        }
      }
    });
  }

  // Tüm sayfalara footer ekle
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc);
  }

  // PDF'i yeni sekmede aç
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};
